import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AiProofButton, AiProofResult, useAiProof } from './AiProof.js';
import { RichTextEditor } from './RichTextEditor.js';
import { MarkdownBody } from './MarkdownBody.js';
import { useState } from 'react';
import {
  ArrowUpRight,
  Hash,
  Layers,
  MessageSquare,
  Plane,
  Route,
  Send,
  Timer,
  UserRound,
  X,
} from 'lucide-react';
import { api, type TenantConfig, type TicketDetail } from '../lib/api.js';
import { ownerOptions } from '../lib/assignee.js';
import { awayLabel } from '../lib/away.js';
import { TicketSummary } from './TicketSummary.js';
import { ViewingNow } from './ViewingNow.js';
import { dateTime, hours, minutes, personLabel } from '../lib/format.js';
import { shortRequestType } from '../lib/labels.js';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  ConfidenceBadge,
  ErrorState,
  Select,
  Skeleton,
  SlaBadge,
  StateBadge,
  Textarea,
  Tooltip,
  VipBadge,
  cn,
  useToast,
} from '../ui/index.js';

/**
 * The peek pane.
 *
 * Selecting a row opens the ticket beside the queue instead of navigating away
 * from it — which is what triage actually is: read, decide, act, next. The
 * full page still exists for the long history; this is for the ninety percent
 * of looks that end in a reply or a reassignment.
 */
export function TicketPeek({
  tenant,
  number,
  onClose,
}: {
  tenant: string;
  number: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reply, setReply] = useState('');
  const proof = useAiProof(tenant, number);
  /**
   * Who else has this open (AG-14). The pane counts as having it open: it is
   * how most tickets get read and answered, so two agents each in a pane are
   * exactly the collision presence exists to prevent, and without a beat
   * from here neither of them, nor anybody on the full page, saw the other.
   */
  const [viewers, setViewers] = useState<Parameters<typeof ViewingNow>[0]['viewers']>([]);

  const q = useQuery({
    queryKey: ['ticket', tenant, String(number)],
    queryFn: () => api.get<TicketDetail>(`/api/v1/tenants/${tenant}/tickets/${number}`),
  });
  const cfg = useQuery({
    queryKey: ['config', tenant],
    queryFn: () => api.get<TenantConfig>(`/api/v1/tenants/${tenant}/config`),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['ticket', tenant, String(number)] });
    void qc.invalidateQueries({ queryKey: ['tickets'] });
    void qc.invalidateQueries({ queryKey: ['queue-counts', tenant] });
  };

  const send = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tenants/${tenant}/tickets/${number}/conversation`, {
        kind: 'reply',
        body: reply,
      }),
    onSuccess: () => {
      setReply('');
      invalidate();
      toast.success(`Replied on #${number}`, 'The requester has been emailed.');
    },
    onError: (e) => toast.error('Could not reply', e instanceof Error ? e.message : undefined),
  });

  const assign = useMutation({
    mutationFn: (oid: string | null) =>
      api.patch(`/api/v1/tenants/${tenant}/tickets/${number}`, { assigneeOid: oid }),
    onSuccess: () => {
      invalidate();
      toast.success('Owner updated');
    },
    onError: (e) => toast.error('Could not reassign', e instanceof Error ? e.message : undefined),
  });

  const d = q.data;
  const t = d?.ticket;
  const paused = t?.state.startsWith('pending_') ?? false;
  const lastInbound = d?.conversation.filter((c) => c.kind !== 'note').at(-1);

  return (
    <>
    {/* Narrower than xl there is no room beside the list, so the pane slides
        over it instead. It used to be hidden there outright: on a laptop or a
        phone, clicking a ticket did nothing visible at all. */}
    {/* Under the top bar, not over it: the page sits in its own stacking
        context below the bar, and the bar stays usable anyway. */}
    <div
      aria-hidden
      className="fixed inset-x-0 bottom-0 top-topbar z-40 bg-black/40 backdrop-blur-[2px] xl:hidden"
      onClick={onClose}
    />
    <aside
      aria-label={`Ticket #${number} preview`}
      className={cn(
        // Floating over the list it needs the solid floating surface (in the
        // glass theme, bg-surface is a faint tint the list shows through).
        'flex shrink-0 flex-col border-l border-line bg-surface-raised xl:bg-surface',
        'animate-[slide-in-right_var(--dur)_var(--ease-out)]',
        'fixed bottom-0 right-0 top-topbar z-50 w-full shadow-xl sm:w-[25rem]',
        // From xl, pinned beside the list rather than flowing with it.
        // `self-start` stops the flex row stretching it, which is what a
        // sticky child needs to be allowed to move at all; top-0 is the
        // underside of the topbar, because the scroll container starts there.
        // Its own height is set explicitly so the body inside can scroll on
        // its own — a long ticket must not push the pane past the bottom of
        // the window.
        'xl:sticky xl:bottom-auto xl:top-0 xl:z-auto xl:self-start xl:shadow-none',
        'xl:h-[calc(100dvh-var(--topbar-h))]',
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="nums text-xs font-medium text-brand">#{number}</span>
            {t && <StateBadge state={t.state} />}
          </div>
          {q.isLoading ? (
            <Skeleton className="mt-2 h-4 w-4/5" />
          ) : (
            <h2 className="mt-1 text-sm font-semibold leading-snug text-ink">{t?.subject}</h2>
          )}
          {/* Renders nothing when nobody else is here. Moving to the next
              row re-subscribes, which leaves the last ticket at once. */}
          <div className={cn(viewers.length > 0 && 'mt-1.5')}>
            <ViewingNow tenant={tenant} number={number} viewers={viewers} onViewers={setViewers} />
          </div>
        </div>
        <Link to={`/t/${tenant}/${number}`}>
          <Tooltip content="Open the full ticket">
            <Button variant="ghost" size="sm" iconOnly aria-label="Open full ticket">
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </Tooltip>
        </Link>
        <Button variant="ghost" size="sm" iconOnly aria-label="Close preview" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {q.error && (
          <div className="p-4">
            <ErrorState error={q.error} />
          </div>
        )}

        {q.isLoading && (
          <div className="space-y-3 p-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        )}

        {d && t && (
          <>
            {/* Blocked banner first — it is the most actionable fact. */}
            {(() => {
              const b = d.periods.find(
                (p) => p.exited_at === null && p.state.startsWith('pending_'),
              );
              if (!b) return null;
              return (
                <div className="border-b border-line px-4 py-3">
                  <Callout tone="accent" title="Blocked — clock paused">
                    Since {dateTime(b.entered_at)}
                    {b.blocked_party && (
                      <>
                        {' '}
                        on <strong>{b.blocked_party}</strong>
                      </>
                    )}
                    {b.reason && <> — {b.reason}</>}
                  </Callout>
                </div>
              );
            })()}

            {/* Facts grid */}
            <dl className="grid grid-cols-2 gap-x-3 gap-y-3 border-b border-line px-4 py-3.5">
              <Fact label="SLA">
                <SlaBadge
                  breached={t.resolution_breached}
                  dueAt={t.resolution_due_at}
                  paused={paused}
                  state={t.state}
                />
              </Fact>
              <Fact label="Worked">
                <span className="nums inline-flex items-center gap-1 text-ink">
                  <Timer aria-hidden className="h-3 w-3 text-ink-tertiary" />
                  {minutes(t.running_minutes)}
                </span>
              </Fact>
              <Fact label="Type">
                <Tooltip content={t.request_type ?? 'Unclassified'}>
                  <span className="truncate text-ink">{shortRequestType(t.request_type)}</span>
                </Tooltip>
              </Fact>
              <Fact label="Queue">
                <span className="truncate text-ink">{d.queue?.name ?? '—'}</span>
              </Fact>
              <Fact label="Requester">
                <span className="flex min-w-0 items-center gap-1.5">
                  <Avatar name={d.requester?.name} email={d.requester?.email} size="xs" />
                  {d.requester?.vip && <VipBadge title={d.requester.job_title} />}
                  <span className="truncate text-ink">
                    {personLabel(d.requester?.name, d.requester?.email)}
                  </span>
                </span>
              </Fact>
              <Fact label="Routing">
                <ConfidenceBadge
                  confidence={t.route_confidence}
                  needsHuman={t.needs_human_routing}
                />
              </Fact>

              {d.subjectPerson && (
                <Fact label="About" span>
                  <Badge tone="info" icon={<UserRound />}>
                    {personLabel(d.subjectPerson.name, d.subjectPerson.email)}
                  </Badge>
                </Fact>
              )}

              {t.project_numbers.length > 0 && (
                <Fact label="Project" span>
                  <span className="nums inline-flex items-center gap-1 font-mono text-xs text-ink">
                    <Hash aria-hidden className="h-3 w-3 text-ink-tertiary" />
                    {t.project_numbers.join(', ')}
                  </span>
                </Fact>
              )}

              {t.systems.length > 0 && (
                <Fact label="Systems" span>
                  <span className="flex flex-wrap gap-1">
                    {t.systems.slice(0, 4).map((s) => (
                      <Badge key={s} tone="neutral" icon={<Layers />}>
                        {s}
                      </Badge>
                    ))}
                  </span>
                </Fact>
              )}
            </dl>

            {/* Owner */}
            <div className="border-b border-line px-4 py-3">
              <p className="mb-1.5 text-3xs font-semibold uppercase tracking-wide text-ink-tertiary">
                Owner
              </p>
              <Select
                size="sm"
                aria-label="Assign owner"
                value={t.assignee_oid ?? ''}
                onValueChange={(v) => assign.mutate(v || null)}
                options={ownerOptions(
                  { oid: t.assignee_oid, name: t.assignee_name, email: t.assignee_email },
                  cfg.data?.agents,
                )}
              />
              {d?.assignee?.away && (
                <p className="mt-1 flex items-center gap-1 text-2xs text-warn">
                  <Plane aria-hidden className="h-3 w-3" />
                  {awayLabel(d.assignee.awayUntil)}
                </p>
              )}
            </div>

            {/* The brief, above the thread it was written from. */}
            <div className="border-b border-line px-4 py-3">
              <TicketSummary tenant={tenant} number={number} compact />
            </div>

            {/* Latest message */}
            {lastInbound && (
              <div className="border-b border-line px-4 py-3">
                <p className="mb-1.5 flex items-center gap-1.5 text-3xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  <MessageSquare aria-hidden className="h-3 w-3" />
                  Latest · {d.conversation.length} messages
                </p>
                <div className="rounded-md border border-line-subtle bg-surface-sunken/60 p-2.5">
                  <p className="mb-1 flex items-center gap-1.5 text-2xs text-ink-secondary">
                    <Avatar
                      name={lastInbound.author_name}
                      email={lastInbound.author_email ?? lastInbound.author_label}
                      size="xs"
                    />
                    <span className="truncate font-medium">
                      {personLabel(
                        lastInbound.author_name,
                        lastInbound.author_email ?? lastInbound.author_label,
                      )}
                    </span>
                    <span className="ml-auto shrink-0 text-ink-tertiary">
                      {dateTime(lastInbound.created_at)}
                    </span>
                  </p>
                  {lastInbound.kind === 'inbound' ? (
                    <p className="line-clamp-6 whitespace-pre-wrap text-xs leading-relaxed text-ink">
                      {lastInbound.body}
                    </p>
                  ) : (
                    <MarkdownBody text={lastInbound.body} className="line-clamp-6 text-xs" />
                  )}
                </div>
              </div>
            )}

            {/* Classification provenance */}
            {d.classification[0] && (
              <div className="border-b border-line px-4 py-3">
                <p className="mb-1.5 flex items-center gap-1.5 text-3xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  <Route aria-hidden className="h-3 w-3" />
                  {d.classification[0].rung === null
                    ? `Classified on arrival, from ${d.classification[0].source}`
                    : `Classified at rung ${d.classification[0].rung}`}
                </p>
                <p className="text-2xs leading-relaxed text-ink-secondary">
                  {d.classification[0].matched_rule ? (
                    <>
                      Matched{' '}
                      <code className="rounded-xs bg-surface-sunken px-1 font-mono text-3xs text-ink">
                        {d.classification[0].matched_rule}
                      </code>
                    </>
                  ) : (
                    <>Proposed {d.classification[0].proposed_request_type ?? 'no type'}</>
                  )}
                  {d.classification[0].human_request_type && (
                    <>
                      {' '}
                      · an agent changed the type to{' '}
                      <strong className="text-ink">
                        {d.classification[0].human_request_type}
                      </strong>
                    </>
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reply composer, pinned */}
      {d && (
        <div className="border-t border-line bg-surface-sunken/40 p-3">
          <RichTextEditor
            ariaLabel="Reply"
            placeholder="Reply to the requester…"
            value={reply}
            onChange={setReply}
            disabled={send.isPending}
            onSubmit={() => {
              if (reply.trim()) send.mutate();
            }}
          />
          {proof.result && (
            <AiProofResult
              result={proof.result}
              current={reply}
              onUse={(text) => {
                setReply(text);
                proof.clear();
              }}
              onKeep={proof.clear}
            />
          )}
          <div className="mt-2 flex items-center justify-between gap-2">
            <Link to={`/t/${tenant}/${number}`} className="text-2xs text-ink-tertiary hover:text-ink-secondary">
              Full ticket, timeline and audit →
            </Link>
            <div className="flex items-center gap-1">
              <AiProofButton
                pending={proof.run.isPending}
                disabled={reply.trim().length === 0}
                onClick={() => proof.run.mutate({ text: reply, kind: 'reply' })}
              />
              <Button
                variant="primary"
                size="sm"
                leading={<Send className="h-3 w-3" />}
                loading={send.isPending}
                disabled={reply.trim().length === 0}
                onClick={() => send.mutate()}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </aside>
    </>
  );
}

function Fact({
  label,
  children,
  span,
}: {
  label: string;
  children: React.ReactNode;
  span?: boolean;
}) {
  return (
    <div className={cn('min-w-0', span && 'col-span-2')}>
      <dt className="mb-1 text-3xs font-semibold uppercase tracking-wide text-ink-tertiary">
        {label}
      </dt>
      <dd className="min-w-0 text-xs">{children}</dd>
    </div>
  );
}
